import { useEffect, useState } from 'react';

/**
 * Tracks the height of the visual viewport (the area NOT covered by the
 * on-screen keyboard). On iOS WKWebView and Android WebView the layout viewport
 * (window.innerHeight / 100vh) does NOT shrink when the software keyboard opens,
 * so a bottom-pinned button would sit behind the keyboard. Binding a container's
 * height to visualViewport.height instead makes it shrink with the keyboard, so
 * anything anchored to the bottom (e.g. the login button) floats above it.
 *
 * Returns undefined when visualViewport is unavailable so callers can fall back
 * to a CSS height (100dvh/100vh).
 */
export function useVisualViewportHeight(): number | undefined {
  const vv = typeof window !== 'undefined' ? window.visualViewport : undefined;
  const [height, setHeight] = useState<number | undefined>(vv?.height);

  useEffect(() => {
    if (!vv) return undefined;
    const update = () => setHeight(vv.height);
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [vv]);

  return height;
}
